import React, { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import Swal from 'sweetalert2';
import jwt_decode from 'jwt-decode';
import { InventoryOrdersTable } from './components/InventoryOrdersTable';
import { AddEditOrderModalWrapper } from './Modals/AddEditOrderModalWrapper';
import { DeleteConfirmationModal } from './Modals/DeleteConfirmationModal';
import { createInventoryOrder, updateInventoryOrder, deleteInventoryOrder, getPropertyList, getInventoryItems, getInventoryOrders, getInventoryOrdersForRMVendor } from './utils/api';
import { getAllRMVendors } from '../RMVendors/Helpers/Api';
import { PropertySelector } from './components/PropertySelector';
import { getValidationSchema, getInitialValues } from './Helpers/Config';
import './styles/styles.css';
import { DeliveryStatus, OrderStatus } from './constants/statuses';

const InventoryOrdersForRMVendors = () => {
  const dispatch = useDispatch();
  const [modal, setModal] = useState('');
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [properties, setProperties] = useState([]);
  const [rmVendors, setRMVendors] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [vendorCategories, setVendorCategories] = useState([]);
  const [user, setUser] = useState(null); // Add state for user

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch (error) {
      return null;
    }
  }, []);

  useEffect(() => {
    const currentUser = getUserFromToken();
    setUser(currentUser);
  }, [getUserFromToken]);

  const fetchProperties = useCallback(async () => {
    if (!user) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await getPropertyList(user?.role || '', user?.id || '');
      setProperties(result?.propertyList || []);
    } catch (error) {
      setProperties([]);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, user]);

  const fetchRMVendors = useCallback(async (propertyId) => {
    if (!propertyId) {
      setRMVendors([]);
      setVendorCategories([]);
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await getAllRMVendors(propertyId);
      setRMVendors(result?.data || []);
      setVendorCategories(result?.categories || []);
    } catch (error) {
      setRMVendors([]);
      setVendorCategories([]);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const fetchInventoryItems = useCallback(async (propertyId, buildingName) => {
    if (!propertyId || !buildingName) {
      setInventoryItems([]);
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await getInventoryItems(propertyId, buildingName);
      setInventoryItems(response?.items || []);
    } catch (error) {
      setInventoryItems([]);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Failed to fetch inventory.'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const fetchOrders = useCallback(async (propertyId, buildingName) => {
    if (!user || user.role !== 'rm vendor' || !user.id) {
      setOrders([]);
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const filters = {
        propertyId,
        buildingName,
        page: 1,
        limit: 10,
        sortBy: 'orderDetails.ordered.date',
        sortOrder: 'desc'
      };
      const response = await getInventoryOrdersForRMVendor(user.id, filters);
      setOrders(response?.orders || []);
    } catch (error) {
      setOrders([]);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Failed to fetch vendor orders.'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, user]);

  const formik = useFormik({
    initialValues: getInitialValues('add', null, {}),
    validationSchema: getValidationSchema('add'),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        if (!user) throw new Error('User not authenticated');

        const payload = {
          propertyId: values.propertyId,
          buildingName: values.buildingName,
          vendorId: user.id,
          orderedItems: values.orderedItems || [],
          delivery: values.delivery || {
            status: values.delivery.status || DeliveryStatus.PENDING,
            comments: values.delivery.comments || ''
          },
          status: values.status || OrderStatus.PENDING,
          comments: values.comments,
        };

        const result = await createInventoryOrder(payload);
        if (!result.success) throw new Error(result.message || 'Failed to create inventory order');

        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Inventory order created successfully!'
        });

        await fetchOrders(values.propertyId, values.buildingName);
        resetForm({ values: getInitialValues('add', null, { propertyId: values.propertyId, buildingName: values.buildingName }) });
        setModal('');
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to create inventory order.'
        });
      } finally {
        setSubmitting(false);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  });

  const openViewModal = useCallback((order) => {
    setSelectedOrder(order);
    formik.setValues(getInitialValues('view', order, {}));
    setModal('view');
  }, [formik]);

  const openEditModal = useCallback((order) => {
    setSelectedOrder(order);
    formik.setValues(getInitialValues('edit', order, {}));
    setModal('edit');
  }, [formik]);

  const openDeleteModal = useCallback((order) => {
    setSelectedOrder(order);
    setModal('delete');
  }, []);

  const closeModal = useCallback(() => {
    setModal('');
  }, []);

  const handleEditSubmit = useCallback(async (values, { setSubmitting }) => {
    try {
      if (!selectedOrder?._id) throw new Error('Missing order ID');

      dispatch({ type: 'SET_LOADING', payload: true });
      await updateInventoryOrder(selectedOrder._id, values);

      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Order updated successfully'
      });

      await fetchOrders(values.propertyId, values.buildingName);
      closeModal();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update order'
      });
    } finally {
      setSubmitting(false);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, selectedOrder, fetchOrders, closeModal]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      if (!selectedOrder?._id) throw new Error('No order selected');

      dispatch({ type: 'SET_LOADING', payload: true });
      await deleteInventoryOrder(selectedOrder._id);

      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Order deleted successfully'
      });

      await fetchOrders(formik.values.propertyId, formik.values.buildingName);
      closeModal();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to delete order'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, selectedOrder, fetchOrders, formik.values, closeModal]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    const { propertyId, buildingName } = formik.values;
    if (propertyId) {
      fetchRMVendors(propertyId);
    } else {
      setRMVendors([]);
      setVendorCategories([]);
    }
    if (propertyId && buildingName) {
      fetchInventoryItems(propertyId, buildingName);
      fetchOrders(propertyId, buildingName);
    } else {
      setInventoryItems([]);
      setOrders([]);
    }
  }, [formik.values.propertyId, formik.values.buildingName, fetchRMVendors, fetchInventoryItems, fetchOrders]);

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Rentdigicare | Inventory Orders - RM Vendors</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs
            title="Inventory"
            breadcrumbItem="Orders - RM Vendors"
          />
          <PropertySelector
            formik={formik}
            propertyList={properties}
          />
          <InventoryOrdersTable
            orders={orders}
            onViewDetails={openViewModal}
            onEdit={openEditModal}
            onDelete={openDeleteModal}
            isRMVendor={user?.role === 'rm vendor'}
            onSuccess={() => fetchOrders(formik.values.propertyId, formik.values.buildingName)}
          />
          <AddEditOrderModalWrapper
            mode={modal}
            open={modal === 'add' || modal === 'view' || modal === 'edit'}
            onClose={closeModal}
            onAdd={formik.handleSubmit}
            onEdit={handleEditSubmit}
            formik={formik}
            rmVendors={rmVendors}
            vendorItems={inventoryItems}
            vendorCategories={vendorCategories}
          />
          <DeleteConfirmationModal
            open={modal === 'delete'}
            onClose={closeModal}
            onDelete={handleDeleteConfirm}
            order={selectedOrder}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export { InventoryOrdersForRMVendors };